


























































































































































import { Component, Vue, Prop, PropSync, Provide, Ref, Watch, Inject } from 'vue-property-decorator';
import { GQL_newInvoiceData_newInvoice } from '@/queries/__generated__/newInvoiceData';
import { GQL_orderUnitSettingsDispaySettingFragments_unitSetting_unitInvoiceSetting_displaySettings } from '@/queries/__generated__/orderUnitSettingsDispaySettingFragments';
import SupplierInfo from '../Shared/SupplierInfo.vue';
import CustomerInfo from '../Shared/CustomerInfo.vue';
import { makeReactiveProperty } from '@/utils/utils';
import DocumentPage from '../Shared/DocumentPage.vue';
import LineHeader from '../Shared/LineHeader.vue';
import { IRules } from '@/components/UI/Form/ValidationModule';
import SForm from '@/components/UI/Form/SForm.vue';
import DocumentLogo from '../Shared/DocumentLogo.vue';
import DocumentHeader, { DocumentShowType } from '../Shared/DocumentHeader.vue';
import DocumentDelivery from '../Shared/DocumentDelivery.vue';
import DocumentFooter from '../Shared/DocumentFooter.vue';
import InvoiceSummary from './InvoiceSummary.vue';
import InvoiceLine from './InvoiceLine.vue';
import InvoiceOrderHeader from './InvoiceOrderHeader.vue';
import InvoiceLineComment, { IInvoiceLineComment } from './InvoiceLineComment.vue';
import InvoiceOrderSeparator from './InvoiceOrderSeparator.vue';
import InvoiceOrderAllowanceCharge from './InvoiceOrderAllowanceCharge.vue';
import InvoiceInfo from './InvoiceInfo.vue';
import InvoiceLineProductDescription from './InvoiceLineProductDescription.vue';
import InvoiceOverdueNoticeLine from './InvoiceOverdueNoticeLine.vue';
import { validateRequired, validateType } from '../../UI/Form/Validations';
import TabTitle from '@/utils/tabtitle';
import { GQL_invoice_invoice_invoiceOverdueNotices } from '@/queries/__generated__/invoice';
import InvoiceNoteLine from './InvoiceNoteLine.vue';

interface IUnbreakableElement {
  index: number;
  type: string;
  page: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

@Component({
  components: {
    DocumentPage,
    SupplierInfo,
    CustomerInfo,
    LineHeader,
    DocumentLogo,
    DocumentHeader,
    DocumentDelivery,
    DocumentFooter,
    InvoiceSummary,
    InvoiceLine,
    InvoiceOrderHeader,
    InvoiceLineComment,
    InvoiceOrderSeparator,
    InvoiceOrderAllowanceCharge,
    InvoiceInfo,
    InvoiceLineProductDescription,
    InvoiceOverdueNoticeLine,
    InvoiceNoteLine,
  },
})
export default class Invoice extends Vue {
  @Prop(Object) public invoice!: GQL_newInvoiceData_newInvoice;

  @Provide() public documentComponent: Invoice = this;
  @Provide() public documentType = 'invoice';
  @Provide()
  @Prop({ type: String, default: 'invoice', validator: value => [ 'invoice', 'overdueNotice' ].includes(value) })
  private documentShowType!: DocumentShowType;

  @Inject({ from: 'isPdf', default: false }) public isPdf!: boolean;

  @Ref() public readonly documentPageRef!: DocumentPage[];
  @Ref() public readonly formRef?: SForm;

  public totalPages = 1;
  public reactiveProps: {
    editable?: boolean;
  } = { editable: undefined };

  public mutationObserver!: MutationObserver;
  public elementsArr: IUnbreakableElement[] = [];

  @PropSync('dirty', { type: Boolean, default: false }) public dirtyFormSync!: boolean;

  @PropSync('valid', { type: Boolean, default: true }) public validFormSync!: boolean;
  public rules: IRules = {
    issueDate: [
      {
        validation: validateRequired(),
        message: i18n => i18n.t('Error.required', { field: i18n.t('Invoice.issueDate') }),
      },
      {
        validation: validateType('date'),
        message: i18n => i18n.t('GQL.Type.date'),
      },
    ],
    dueDate: [
      {
        validation: validateRequired(),
        message: i18n => i18n.t('Error.required', { field: i18n.t('Invoice.dueDate') }),
      },
      {
        validation: ({ dataValue, formElements }) => {
          if (!formElements?.issueDate?.dataValue || !dataValue) return true;
          return formElements.issueDate.dataValue <= dataValue;
        },
        message: i18n => i18n.t('Error.mustBeSameOrAfter', {
          beforeField: i18n.t('Invoice.issueDate'),
          afterField: i18n.t('Invoice.dueDate'),
        }),
        triggerBy: [ 'issueDate' ],
      },
      {
        validation: validateType('date'),
        message: i18n => i18n.t('GQL.Type.date'),
      },
    ],
  };

  public created() {
    TabTitle.setFields({ number: this.invoice.number });
    makeReactiveProperty(this, this.reactiveProps, 'editable', 'editable');
    this.buildElementsArray();
  }

  public mounted() {
    TabTitle.setFields({ number: this.invoice.number });
    this.setElementsPageNumbers();
    this.mutationObserver = new MutationObserver(this.setElementsPageNumbers);
    this.mutationObserver.observe((this.$el.querySelector('.paginated') as HTMLElement), { childList: true });
  }

  public get editable() {
    return !this.invoice.id;
  }

  public buildElementsArray() {
    let index = 0;
    const arr: IUnbreakableElement[] = [];

    this.invoice.orders.forEach(order => {
      arr.push({ index: index++, type: 'InvoiceOrderHeader', page: 1, order });

      order.orderLines.forEach(line => {
        arr.push({ index: index++, type: 'OrderLine', page: 1, line });

        if (line.note) {
          const comment: IInvoiceLineComment = {
            text: line.note,
            productNumber: line.sellersItemIdentificationId,
          };
          arr.push({ index: index++, type: 'LineComments', page: 1, comment });
        }

        if (line.productDescriptionEnabled && line.productDescription) {
          arr.push({
            index: index++,
            type: 'InvoiceLineProductDescription',
            page: 1,
            productDescription: {
              productNumber: line.sellersItemIdentificationId,
              text: line.productDescription,
            },
          });
        }
      });

      order.orderAllowanceCharges.forEach(ac => {
        if (!ac.percent.isZero()) {
          arr.push({ index: index++, type: 'OrderAllowanceCharge', page: 1, ac });
        }
      });

      arr.push({ index: index++, type: 'Separator', page: 1 });
    });

    if (this.documentShowType === 'overdueNotice') {
      this.invoice.invoiceOverdueNotices.filter(notice => [ 1,2 ].includes(notice.orderNumber)).forEach(notice => {
        arr.push({ index: index++, type: 'InvoiceOverdueNoticeLine', page: 1, notice });
      });
    }

    this.invoice.legalNotes.forEach(note => {
      arr.push({ index: index++, type: 'InvoiceNoteLine', page: 1, note });
    });

    if (this.invoice.customNote) {
      arr.push({ index: index++, type: 'InvoiceNoteLine', page: 1, note: this.invoice.customNote });
    }

    // Stating the interest terms on the invoice itself, while the unit charges
    // interest. The server fills it in with the rate that was in force when
    // the invoice was issued.
    if (this.invoice.interestNote) {
      arr.push({ index: index++, type: 'InvoiceNoteLine', page: 1, note: this.invoice.interestNote });
    }

    this.elementsArr = arr;
  }

  public getElementsOnPage(page: number) {
    return this.elementsArr.filter(el => el.page === page);
  }

  @Watch('elementsArr', { deep: true })
  @Watch('invoice', { deep: true })
  public setElementsPageNumbers() {
    this.$nextTick(() => {
      // if (observer instanceof MutationObserver) this.mutationObserver.disconnect();
      let currentPage = 1;
      let lineHeights = 0;
      let stillAvailableOnLastPage = 0;

      // Get page dimensions.
      const pageComponent = this.documentPageRef[0];
      const pageHeight = (pageComponent.$el as HTMLElement).offsetHeight;
      const paddingTop = pageComponent.padding;
      const paddingBottom = pageComponent.padding;

      const tableHeader = this.$el.querySelector('.paginated thead') as HTMLElement;
      if (!tableHeader) return;

      const tableHeaderHeight = tableHeader.offsetHeight;

      // Page height after padding is removed.
      const PAGE_HEIGHT = pageHeight - paddingTop - paddingBottom - tableHeaderHeight;

      // Loop through elements
      this.elementsArr.forEach(unbreakable => {
        const elementGroup = this.$el.querySelectorAll(`.unbreakable_${unbreakable.index}`);

        // Get the full extended line's height.
        const lineHeight = Array.from(elementGroup).reduce((sum, el) => sum + (el as HTMLElement).offsetHeight, 0);

        // Get header height.
        const header = this.$el.querySelector(`.top[data-page="${currentPage}"]`) as HTMLElement;
        const headerHeight = header ? header.offsetHeight : 0;

        // Available space is total page excluding the header.
        const available = PAGE_HEIGHT - headerHeight;

        // Current line's height is added to the total height of lines on the current page.
        lineHeights += lineHeight;

        // If the total height of lines is bigger than available
        if (lineHeights > available) {
          lineHeights = lineHeight;
          this.totalPages = currentPage += 1;
        }

        // Assign the page number.
        Vue.set(unbreakable, 'page', currentPage);

        // These are only relevant in the last iteration through the loop.
        // Get the left available space on the last page.
        stillAvailableOnLastPage = available - lineHeights;
      });

      // Get footer height.
      const footer = this.$el.querySelector(`.document-footer`) as HTMLElement;
      const footerHeight = footer ? footer.offsetHeight : 0;

      // If the is more available space on current page than the footer height, this is the last page.
      if (stillAvailableOnLastPage >= footerHeight) {
        this.totalPages = currentPage;
      } else {
        this.totalPages = currentPage + 1;
      }
    });
  }

  get displaySettings(): GQL_orderUnitSettingsDispaySettingFragments_unitSetting_unitInvoiceSetting_displaySettings {
    return this.invoice.unit.unitSetting.unitInvoiceSetting.displaySettings;
  }

  get lastOverdueNotice(): GQL_invoice_invoice_invoiceOverdueNotices | null {
    return this.invoice.invoiceOverdueNotices[this.invoice.invoiceOverdueNotices.length - 1];
  }

  public get displayLineAllowanceChargeColumn(): boolean {
    return (
      this.displaySettings.invoiceLineAllowanceCharge ||
      this.invoice.orders.some(order =>
        order.orderLines.some(line =>
          line.orderLineAllowanceCharge &&
          !line.orderLineAllowanceCharge.percent.isZero(),
        ),
      )
    );
  }
}
